import { useEffect, useRef, type MutableRefObject } from 'react';
import type { EditorIntent } from './editorIntent';
import type { EditorMark } from './editorVisit';

// The two keys the canvas answers, and deliberately only two. Escape lets go of the selection, and
// Delete asks the builder to remove it — the same request the toolbar's own button posts, so there is
// one path to removal and not two. Arrow keys are left alone on purpose: nudging a node by a pixel is
// not a thing a merchant wants from a page builder, and reordering by keyboard belongs in the tree
// where the merchant can see what moved.

/** What the canvas needs in order to answer a keystroke, held as one shape so nothing takes five arguments. */
export type EditorKeys = {
  /** What is selected at the moment the key is pressed, read through a ref so nothing rebinds per render. */
  selection: MutableRefObject<EditorMark>;
  /** Lets go of the selection here and tells the builder, so the rail and the canvas agree on nothing. */
  deselect: () => void;
  sendIntent: (intent: EditorIntent) => void;
};

/** The keys that mean "remove this", both of which a browser will otherwise put to its own use. */
const REMOVE_KEYS: readonly string[] = ['Delete', 'Backspace'];

// Reports whether a keystroke belongs to something being typed into rather than to the page itself.
const isTyping = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

// Reports whether anything is selected for a key to act on, at whatever depth the merchant picked it.
const hasSelection = (mark: EditorMark): boolean => mark.elementId !== null;

/**
 * Answers Escape and Delete over the canvas. A keystroke another handler has already claimed is left
 * alone — the inline editor takes Escape to throw an edit away, and that must not also empty the
 * selection behind it — and so is one aimed at a field the merchant is typing in.
 */
export function useEditorKeys(active: boolean, keys: EditorKeys): void {
  const held = useRef(keys);
  held.current = keys;

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTyping(event.target)) return;
      const { selection, deselect, sendIntent } = held.current;
      if (event.key === 'Escape') {
        event.preventDefault();
        return deselect();
      }
      if (!REMOVE_KEYS.includes(event.key) || !hasSelection(selection.current)) return;
      event.preventDefault();
      sendIntent({ kind: 'delete', ...selection.current });
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active]);
}
